import { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Field } from '~/components/forms';
import { FieldName, getInputProps, useField } from '@conform-to/react';

export type ExtractedPlace = {
  city: string;
  countryCode: string;
  neighborhood: string;
  placeId: string;
  lat: string;
  lng: string;
};

export function extractPlace(place: google.maps.places.PlaceResult): ExtractedPlace {
  const comp = (type: string) =>
    place.address_components?.find((c) => c.types.includes(type));
  const city =
    comp('locality')?.long_name ?? comp('postal_town')?.long_name ?? '';
  const countryCode = comp('country')?.short_name ?? '';
  const neighborhood =
    comp('neighborhood')?.long_name ??
    comp('sublocality')?.long_name ??
    comp('sublocality_level_1')?.long_name ??
    '';
  return {
    city,
    countryCode,
    neighborhood,
    placeId: place.place_id ?? '',
    lat: place.geometry?.location?.lat()?.toString() ?? '',
    lng: place.geometry?.location?.lng()?.toString() ?? '',
  };
}

interface PlaceResult extends google.maps.places.PlaceResult {
  formatted_address?: string;
  geometry?: {
    location: google.maps.LatLng;
    viewport?: google.maps.LatLngBounds;
  };
  address_components?: google.maps.GeocoderAddressComponent[];
}

type PlaceAutocompleteProps = {
  onPlaceSelect: (place: PlaceResult) => void
  name: FieldName<string>
  title?: string
  placeholder?: string
  additionalErrors?: string[]
}

const PlaceAutocomplete = ({ onPlaceSelect, name, title, placeholder, additionalErrors }: PlaceAutocompleteProps) => {
  const [meta] = useField(name);
  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");
  const initialValue = useRef<string | null>(null);
  // formatted_address of the last Google suggestion picked, so the blur-time geocode fallback
  // below doesn't re-resolve (and possibly overwrite) a place the user already chose.
  const lastSelectedAddress = useRef<string | null>(null);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options: google.maps.places.AutocompleteOptions = {
      fields: ["address_components", "geometry", "name", "formatted_address", "place_id"],
    };

    const autocomplete = new places.Autocomplete(inputRef.current, options);
    setPlaceAutocomplete(autocomplete);

    return () => {
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const listener = placeAutocomplete.addListener("place_changed", () => {
      const place = placeAutocomplete.getPlace() as PlaceResult;
      lastSelectedAddress.current = place.formatted_address ?? null;
      onPlaceSelect(place);
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [onPlaceSelect, placeAutocomplete, meta]);

  // Handle browser autocomplete
  useEffect(() => {
    if (!places || !inputRef.current) return;

    // Store the initial value on first render
    if (initialValue.current === null) {
      initialValue.current = inputRef.current.value || '';
    }

    const handleInputChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const currentValue = input.value;
      
      // Only proceed if:
      // 1. We have a substantial value
      // 2. The value is different from the initial value (to avoid triggering on first load)
      // 3. It isn't the address Google already resolved via place_changed
      if (currentValue &&
          currentValue.length > 3 &&
          currentValue !== initialValue.current &&
          currentValue !== lastSelectedAddress.current) {
        
        // Use a small timeout to ensure we're catching the final value after autocomplete
        setTimeout(() => {
          // If the value hasn't changed during the timeout, proceed with geocoding
          if (input.value === currentValue) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: currentValue }, (results, status) => {
              if (status === "OK" && results?.[0]) {
                onPlaceSelect(results[0] as PlaceResult);
              }
            });
          }
        }, 100);
      }
    };

    // Listen for `change` (fires when the value is committed, e.g. on blur) to catch browser
    // autofill. NOT `input`: that fires per keystroke and geocoded partial text, filling the
    // address fields with whatever random place the fragment matched.
    inputRef.current.addEventListener('change', handleInputChange);
    
    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('change', handleInputChange);
      }
    };
  }, [places, onPlaceSelect]);


  // NOTE: this should be removed when all places have well formated address
  // useEffect(() => {
  //   if (!placeAutocomplete || !meta.value) return;

  //   // Search for place when meta.value changes
  //   const geocoder = new google.maps.Geocoder();
  //   geocoder.geocode({ address: meta.value }, (results, status) => {
  //     if (status === "OK" && results?.[0]) {
  //       onPlaceSelect(results[0] as PlaceResult);
  //     }
  //   });
  // }, [meta.value, placeAutocomplete, onPlaceSelect]);

  return (
    <Field
      labelProps={{ htmlFor: meta.id, children: title ?? 'Dirección' }}
      inputProps={{
        ...getInputProps(meta, {type: 'text'}),
        defaultValue: meta.value || '',
        ref: inputRef,
        type: 'text',
        placeholder: placeholder ?? 'Ingrese una dirección',
        // Picking a Google suggestion with Enter would otherwise also trigger the form's
        // implicit submission, saving before `place_changed` has filled the address fields.
        onKeyDown: (e) => {
          if (e.key === 'Enter') e.preventDefault();
        },
      }}
      errors={additionalErrors ? [...(meta.errors || []), ...additionalErrors] : meta.errors}
    />
  );
};

export default PlaceAutocomplete;
